import React from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import '../styles/Pagination.css';

/**
 * Pagination — controle único de paginação da plataforma
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Existia só dentro de Relatórios. A Qualidade do OCR tinha o seu, com
 * "Anterior" e "Próxima" apenas, que só aparecia quando havia mais de uma
 * página e não dizia quantos registros existiam nem permitia trocar o tamanho
 * da página. Os Relatórios de Acesso paginavam no navegador sobre um recorte de
 * 5.000 linhas do servidor.
 *
 * Props:
 *   currentPage, totalPages, pageSize, totalRecords
 *   onPageChange(pagina), onPageSizeChange(tamanho)
 *   approximate  — o total é uma estimativa (filtros derivados no servidor)
 *   itemLabel    — o nome do que está sendo contado ("leitura", "acesso")
 */

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const Pagination = ({
  currentPage, totalPages, pageSize, totalRecords,
  onPageChange, onPageSizeChange,
  approximate = false, itemLabel = 'registro',
}) => {
  if (!totalRecords) return null;

  // Um número inválido chegando aqui virava "Exibindo NaN–NaN" na tela. A conta
  // é aritmética simples e não tem por que confiar em quem chama.
  const numero = (valor, padrao) => (Number.isFinite(Number(valor)) ? Number(valor) : padrao);
  const pagina = Math.max(1, numero(currentPage, 1));
  const tamanho = Math.max(1, numero(pageSize, PAGE_SIZE_OPTIONS[1]));
  const paginas = Math.max(1, numero(totalPages, 1));

  const primeiro = (pagina - 1) * tamanho + 1;
  const ultimo = Math.min(pagina * tamanho, totalRecords);
  const plural = totalRecords === 1 ? itemLabel : `${itemLabel}s`;

  const numerosDePagina = () => {
    const janela = 2;
    const meio = [];
    const lista = [];
    for (let i = Math.max(2, pagina - janela); i <= Math.min(paginas - 1, pagina + janela); i++) meio.push(i);
    if (pagina - janela > 2) lista.push(1, '…'); else lista.push(1);
    lista.push(...meio);
    if (pagina + janela < paginas - 1) lista.push('…', paginas);
    else if (paginas > 1) lista.push(paginas);
    return lista;
  };

  return (
    <nav className="pagination-wrapper" aria-label="Paginação">
      <div className="pagination-info">
        Exibindo <strong>{primeiro}–{ultimo}</strong> de{' '}
        <strong>{approximate ? `~${totalRecords.toLocaleString('pt-BR')}` : totalRecords.toLocaleString('pt-BR')}</strong> {plural}
        {approximate && <span className="pagination-hint" title="Os filtros derivados são aplicados linha a linha; o total é uma estimativa"> (estimado)</span>}
      </div>

      <div className="pagination-controls">
        <button className="pagination-btn" onClick={() => onPageChange(1)}
          disabled={pagina === 1} title="Primeira página" aria-label="Primeira página">
          <FiChevronsLeft size={14} />
        </button>
        <button className="pagination-btn" onClick={() => onPageChange(pagina - 1)}
          disabled={pagina === 1} title="Página anterior" aria-label="Página anterior">
          <FiChevronLeft size={14} />
        </button>

        {numerosDePagina().map((p, i) =>
          p === '…' ? (
            <span key={`e-${i}`} className="pagination-dots" aria-hidden="true">…</span>
          ) : (
            <button
              key={p}
              className={`pagination-btn ${pagina === p ? 'active' : ''}`}
              onClick={() => onPageChange(p)}
              aria-current={pagina === p ? 'page' : undefined}
              aria-label={`Página ${p}`}
            >
              {p}
            </button>
          )
        )}

        <button className="pagination-btn" onClick={() => onPageChange(pagina + 1)}
          disabled={pagina >= paginas} title="Próxima página" aria-label="Próxima página">
          <FiChevronRight size={14} />
        </button>
        <button className="pagination-btn" onClick={() => onPageChange(paginas)}
          disabled={pagina >= paginas} title="Última página" aria-label="Última página">
          <FiChevronsRight size={14} />
        </button>
      </div>

      <div className="pagination-size-selector">
        <label htmlFor="pag-tamanho">Por página</label>
        <select id="pag-tamanho" value={tamanho} onChange={e => onPageSizeChange(Number(e.target.value))}>
          {PAGE_SIZE_OPTIONS.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </div>
    </nav>
  );
};

export default Pagination;
